'use client';

import { WagtailPage } from '@/types';
import Button from '@components/Button';
import { Checkbox, Fieldset, Input } from '@components/Form';
import Link from 'next/link';
// import { useSearchParams } from 'next/navigation';
import { useId, useState } from 'react';

import Select from '@components/Form/Select';
import Honeypot from '@components/Honeypot';
import RawHtml from '@components/RawHtml';
import { removeBaseUrl } from '@utils/url';

type Props = { terms_of_service: WagtailPage; page_id: number; course?: string; thank_you_text?: string };

function InhouseForm(props: Props) {
  const { thank_you_text, terms_of_service, page_id, course } = props;
  const [status, setStatus] = useState('idle');

  const id = useId();
  return (
    <div className='container'>
      <form
        id={`form-${id}`}
        className='inhouse-form'
        onSubmit={async event => {
          event.preventDefault();

          const formData = new FormData(event.currentTarget);

          const data: {
            [key: string]: FormDataEntryValue | FormDataEntryValue[];
          } = Object.fromEntries(formData);

          try {
            setStatus('loading');
            const res = await fetch(`${process.env.NEXT_PUBLIC_WAGTAIL_API_URL}/v2/forms/`, {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                page: page_id,
                form_data: data,
              }),
            });

            if (res.ok) {
              setStatus('success');
            } else {
              setStatus('error');
            }
          } catch (error) {
            setStatus('error');
          }
        }}
      >
        <Fieldset legend='Seminar & Teilnehmer'>
          {course ? (
            <Input
              label='Bitte erstellen Sie mir ein individuelles Inhouse Angebot für folgendes Seminar'
              name='seminar'
              id={`${id}-seminar`}
              defaultValue={course}
              readOnly
              required
            />
          ) : (
            <Input label='Ihr individueller Themenwunsch' name='seminar' id={`${id}-seminar`} required />
          )}
          <Input
            name='participant_count'
            type='number'
            label='Teilnehmeranzahl'
            id={`${id}-participant-count`}
            required
            min='0'
          />
        </Fieldset>
        <Fieldset legend='Ihre Kontaktdaten'>
          <div className='inhouse-form__row'>
            <Select
              id={`${id}-salutation`}
              name='salutation'
              label='Anrede'
              ariaLabel='Anrede'
              items={[
                { text: 'keine Angabe', value: 'keine Angabe' },
                { text: 'Herr', value: 'Herr' },
                { text: 'Frau', value: 'Frau' },
              ]}
            />
            <Input name='title' label='Titel' id={`${id}-title`} />
          </div>
          <div className='inhouse-form__row'>
            <Input name='first_name' label='Vorname' id={`${id}-first-name`} required />
            <Input name='last_name' label='Nachname' id={`${id}-last-name`} required />
          </div>
        </Fieldset>
        <Fieldset legend='Unternehmen'>
          <div className='inhouse-form__row'>
            <Input name='company' label='Firma' id={`${id}-company`} required />
            <Input name='department' label='Abteilung' id={`${id}-department`} />
          </div>
          <Input name='address' label='Unternehmensadresse' id={`${id}-address`} required />
          <div className='inhouse-form__row'>
            <Input name='zip' label='Postleitzahl' id={`${id}-zip`} required />
            <Input name='city' label='Stadt' id={`${id}-city`} required />
          </div>
        </Fieldset>
        <Fieldset legend='Erreichbarkeit'>
          <Input name='telephone' label='Telefon' id={`${id}-telephone`} required />
          <div className='inhouse-form__row'>
            <Input name='email' type='email' label='E-Mail' id={`${id}-email`} required />
            <Input
              name='email_confirmation'
              type='email'
              id={`${id}-email-confirmation`}
              required
              label='Bitte bestätigen Sie Ihre E-Mail-Adresse'
            />
          </div>
        </Fieldset>
        <Select
          id={`${id}-acquisition`}
          name='acquisition'
          label='Wie sind Sie auf uns aufmerksam geworden?'
          ariaLabel='Wie sind Sie auf uns aufmerksam geworden?'
          items={[
            {
              text: ' Printmedien ',
              value: 'Printmedien',
            },
            {
              text: 'Messe/Veranstaltung',
              value: 'Messe/Veranstaltung',
            },
            {
              text: 'Newsletter',
              value: 'Newsletter',
            },
            {
              text: 'Empfehlung',
              value: 'Empfehlung',
            },
            {
              text: 'Social Media',
              value: 'Social Media',
            },
            {
              text: 'Suchmaschinen-Suche',
              value: 'Suchmaschinen-Suche',
            },
            {
              text: 'Schulungs-Portal',
              value: 'Schulungs-Portal',
            },
          ]}
        />
        <div className='inhouse-form__terms-of-service'>
          <Checkbox
            id={`${id}-tos`}
            name='terms_of_service'
            label={
              <>
                Ich habe die{' '}
                <Link href={removeBaseUrl(terms_of_service.full_url)} target='_blank' className='inhouse-form__link'>
                  Teilnahmebedingungen
                </Link>{' '}
                zur Kenntnis genommen.
              </>
            }
            value='accepted'
            required
          />
        </div>
        {status === 'success' ? (
          <div className='message message--success'>
            <RawHtml html={thank_you_text} />
          </div>
        ) : null}
        {status === 'error' ? (
          <div className='message message--error'>
            <p>Leider ist etwas schief gegangen... Bitte versuchen Sie es später erneut...</p>
            {process.env?.NEXT_PUBLIC_CONTACT_EMAIL ? (
              <p>
                Alternativ können Sie Ihre Anfrage auch direkt an die E-Mail-Adresse{' '}
                <a href={`mailto:${process.env.NEXT_PUBLIC_CONTACT_EMAIL}`}>{process.env.NEXT_PUBLIC_CONTACT_EMAIL}</a>{' '}
                senden.
              </p>
            ) : null}
          </div>
        ) : null}
        <Button primary type='submit' disabled={status === 'loading' || status === 'success'}>
          Jetzt anfragen
        </Button>
        <Honeypot />
      </form>
    </div>
  );
}

export default InhouseForm;
